import {v4 as uuidv4} from 'uuid';

import type {ImageMediaType} from '../../client/promptParts';
import type {ImagePart} from '../store/thread';
import type {ComposerImage} from './file';

// Re-exported so component imports of the attachment types keep resolving
// through here.
export type {ComposerFile, ComposerImage, FailedComposerImage} from './file';

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGES_PER_TURN = 10;
// Deliberately below MAX_IMAGE_BYTES * MAX_IMAGES_PER_TURN: users can attach
// up to 10 images, but only ~4 at the per-image max before hitting the total.
export const MAX_TOTAL_IMAGE_BYTES = 20 * 1024 * 1024;
export const ACCEPTED_IMAGE_MEDIA_TYPES: ImageMediaType[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

export type ImagePartResult =
  | {ok: true; part: ImagePart}
  | {ok: false; reason: 'unsupported-type' | 'too-large' | 'read-failed'};

export type ImageValidationReason = 'too-many' | 'total-too-large';

export type ImagePartsValidation =
  | {ok: true}
  | {ok: false; reason: ImageValidationReason};

/**
 * Pull just the image files out of a clipboard/drop `FileList`. Pasting
 * plain text yields none, letting a paste handler fall through to the
 * editor's normal text handling.
 */
export function filterImageFiles(
  files: ArrayLike<File> | null | undefined
): File[] {
  return Array.from(files ?? []).filter(f => f.type.startsWith('image/'));
}

/** Wrap an `ImagePart` with a stable id for composer list management. */
export function toComposerImage(part: ImagePart): ComposerImage {
  return {...part, id: uuidv4()};
}

/** Drop the composer-only `id`, yielding the store/wire `ImagePart`. */
export function toImagePart({id: _id, ...part}: ComposerImage): ImagePart {
  return part;
}

/**
 * Rebuild a `data:` URL for display. We store only the raw base64 payload
 * (prefix stripped for the wire format), so the prefix is re-added here.
 */
export function imagePartToDataUrl(part: ImagePart): string {
  return `data:${part.mediaType};base64,${part.base64Data}`;
}

export function isAcceptedMediaType(type: string): type is ImageMediaType {
  return (ACCEPTED_IMAGE_MEDIA_TYPES as string[]).includes(type);
}

/** Recover the exact decoded (raw) byte count from a base64 payload. */
function base64DecodedBytes(base64Data: string): number {
  const len = base64Data.length;
  if (len === 0) {
    return 0;
  }
  const padding = base64Data.endsWith('==')
    ? 2
    : base64Data.endsWith('=')
      ? 1
      : 0;
  return (len * 3) / 4 - padding;
}

/** Strip the `data:<mime>;base64,` prefix, we want to send only the raw base64 payload. */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const {result} = reader;
      if (typeof result !== 'string') {
        reject(new Error('unexpected FileReader result'));
        return;
      }
      resolve(stripDataUrlPrefix(result));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a user-selected image file into an `ImagePart` for the agent prompt.
 *
 * The bytes are base64-encoded as-is. Callers should surface the failure `reason`.
 */
export async function fileToImagePart(file: File): Promise<ImagePartResult> {
  if (!isAcceptedMediaType(file.type)) {
    return {ok: false, reason: 'unsupported-type'};
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return {ok: false, reason: 'too-large'};
  }
  try {
    const base64Data = await readFileAsBase64(file);
    // Guard against zero-byte payloads; the server rejects empty images anyway.
    if (base64Data.length === 0) {
      return {ok: false, reason: 'read-failed'};
    }
    return {
      ok: true,
      part: {type: 'image', mediaType: file.type, base64Data},
    };
  } catch {
    return {ok: false, reason: 'read-failed'};
  }
}

/**
 * Validate the full set of image parts attached to a turn: enforces the
 * per-turn count and the combined raw (decoded) payload size.
 */
export function validateImageParts(
  parts: readonly ImagePart[]
): ImagePartsValidation {
  if (parts.length > MAX_IMAGES_PER_TURN) {
    return {ok: false, reason: 'too-many'};
  }
  const totalRawBytes = parts.reduce(
    (sum, part) => sum + base64DecodedBytes(part.base64Data),
    0
  );
  if (totalRawBytes > MAX_TOTAL_IMAGE_BYTES) {
    return {ok: false, reason: 'total-too-large'};
  }
  return {ok: true};
}
